import { createContext, useContext, useState, type ReactNode } from "react"

import { getEligibleOfferType } from "@apis/accounts/utils"
import { useAccount } from "@apis/accounts/useAccounts"
import type { IAcceptWinbackOfferResponse } from "@apis/winbacks/types"
import type { IAcceptRetentionOfferResponse } from "@apis/retention/types"
import type { TOfferType } from "@apis/offers/types"

import type { TFunnelOffer } from "./offerTypeGuards"

type Step =
  | "initial"
  | "chooseOffer"
  | "offerDetail"
  | "verification"
  | "cancelMember"
  | "confirmation"

type AccountOfferFunnelContextType = {
  eligibleOfferType: TOfferType | undefined
  step: Step
  setStep: (step: Step) => void
  selectedOffer: TFunnelOffer | null
  setSelectedOffer: (offer: TFunnelOffer | null) => void
  account_id: string
  tpvType: string | null
  setTpvType: (tpv: string) => void
  prospect_guid: string | undefined
  confirmedOrder:
    | IAcceptWinbackOfferResponse
    | IAcceptRetentionOfferResponse
    | null
  setConfirmedOrder: (
    order: IAcceptWinbackOfferResponse | IAcceptRetentionOfferResponse | null,
  ) => void
}

const AccountOfferFunnelContext = createContext<
  AccountOfferFunnelContextType | undefined
>(undefined)

export const AccountOfferFunnelProvider = ({
  children,
  account_id,
}: {
  children: ReactNode
  account_id: string
}) => {
  const getAccountQuery = useAccount(account_id)

  const eligibleOfferType =
    getAccountQuery.data && getEligibleOfferType(getAccountQuery.data)

  const [step, setStep] = useState<Step>("initial")
  const [selectedOffer, setSelectedOffer] = useState<TFunnelOffer | null>(null)
  const [tpvType, setTpvType] = useState<string | null>(null)
  const [confirmedOrder, setConfirmedOrder] = useState<
    IAcceptWinbackOfferResponse | IAcceptRetentionOfferResponse | null
  >(null)

  // eligibleOfferType is derived from the account query, which has no staleTime
  // and so refetches every time a funnel step mounts. If the account changes
  // enough to flip the type, the offer already selected belongs to the other flow
  // and must not be carried over into it (see NEE-4061).
  const [prevEligibleOfferType, setPrevEligibleOfferType] = useState<
    TOfferType | undefined
  >(eligibleOfferType)
  if (eligibleOfferType !== prevEligibleOfferType) {
    setPrevEligibleOfferType(eligibleOfferType)
    setSelectedOffer(null)
    setTpvType(null)
    setStep("initial")
  }

  const prospect_guid = getAccountQuery.data?.prospect_guid

  return (
    <AccountOfferFunnelContext.Provider
      value={{
        eligibleOfferType,
        step,
        setStep,
        selectedOffer,
        setSelectedOffer,
        account_id,
        tpvType,
        setTpvType,
        prospect_guid,
        confirmedOrder,
        setConfirmedOrder,
      }}
    >
      {children}
    </AccountOfferFunnelContext.Provider>
  )
}

export const useAccountOfferFunnel = () => {
  const context = useContext(AccountOfferFunnelContext)
  if (!context) {
    throw new Error(
      "useAccountOfferFunnel must be used inside AccountOfferFunnelProvider",
    )
  }
  return context
}
